import { memo } from 'react';

import { Box, FormControl, FormHelperText, MenuItem, Select, SxProps, Theme } from '@mui/material';

import { useLanguage } from '../../hooks';
import { LangTree } from '../../types';
import { CustomTimePicker } from './customTimePicker';

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '50%',
    margin: 'auto',
  },
  formControlText: {
    marginTop: '12px',
    marginLeft: '0px',
  },
  select: {
    height: '34px',
  },
  menuItem: { fontSize: '12px' },
};

export const getTimeIntervalAsLabel = (lang: LangTree, minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const minutesRest = minutes % 60;
  let label = '';
  if (hours > 0) {
    label += `${hours} ${hours > 1 ? lang.form_builder.hoursLabel : lang.form_builder.hourLabel}`;
    if (minutesRest > 0)
      label += ` ${lang.form_builder.andMinutesLabel} ${minutesRest} ${lang.form_builder.minutesLabel}`;
  } else {
    label += `${minutesRest} ${lang.form_builder.minutesLabel}`;
  }
  return label;
};

interface ActivityPeriodTimeSettingProps {
  questionId: string;
  startTime: string;
  onActivityPeriodTimeSettingChange: (
    id: string,
    value: string,
    action: string,
    tableId?: string,
  ) => void;
  tableId?: string;
  workingTime: string;
  intervalDefault: string;
}

const ActivityPeriodTimeSetting = memo((props: ActivityPeriodTimeSettingProps) => {
  const {
    questionId,
    startTime,
    onActivityPeriodTimeSettingChange,
    tableId,
    workingTime,
    intervalDefault,
  } = props;
  const lang = useLanguage();
  const workingTimeOptions = ['480', '720'];

  const intervalDefaultOptions = ['10', '15', '30', '60'];

  return (
    <Box sx={customStyles.root}>
      <FormControl>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.activityStartTime}
        </FormHelperText>
        <CustomTimePicker
          value={startTime}
          onChange={(value) =>
            onActivityPeriodTimeSettingChange(questionId, value, 'startTime', tableId)
          }
        />
      </FormControl>

      <FormControl>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.activityDuration}
        </FormHelperText>
        <Select
          disableUnderline
          value={workingTime}
          sx={customStyles.select}
          onChange={(e) =>
            onActivityPeriodTimeSettingChange(
              questionId,
              e.target.value.toString(),
              'workingTime',
              tableId,
            )
          }
        >
          {workingTimeOptions.map((workingTimeOption, index) => (
            <MenuItem
              key={index}
              sx={customStyles.menuItem}
              value={parseInt(workingTimeOption)}
            >
              {getTimeIntervalAsLabel(lang, parseInt(workingTimeOption))}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.activityInterval}
        </FormHelperText>
        <Select
          disableUnderline
          value={intervalDefault}
          sx={customStyles.select}
          onChange={(e) =>
            onActivityPeriodTimeSettingChange(
              questionId,
              e.target.value.toString(),
              'intervalDefault',
              tableId,
            )
          }
        >
          {intervalDefaultOptions.map((intervalDefaultOption, index) => (
            <MenuItem
              key={index}
              sx={customStyles.menuItem}
              value={parseInt(intervalDefaultOption)}
            >
              {lang.form_builder.every}{' '}
              {getTimeIntervalAsLabel(lang, parseInt(intervalDefaultOption))}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
});

ActivityPeriodTimeSetting.displayName = 'ActivityPeriodTimeSetting';
export { ActivityPeriodTimeSetting as ActivityPeriodTimeSetting };
